// src/pages/Blog.js - ENHANCED FONTS & ANIMATIONS
import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Calendar,
  User,
  Tag,
  Clock,
  ArrowRight,
  Plus,
  Edit2,
  Trash2,
  Eye,
  ThumbsUp,
  MessageCircle,
  Save,
  X,
  Image as ImageIcon,
  TrendingUp,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function Blog() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // State Management
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showMyBlogs, setShowMyBlogs] = useState(false);
  const [myBlogs, setMyBlogs] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const [blogForm, setBlogForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Technology',
    tags: [],
    coverImage: '',
    status: 'published',
  });

  const [tagInput, setTagInput] = useState('');

  // Memoized categories
  const categories = useMemo(() => [
    'all',
    'Technology',
    'VLSI',
    'AI & ML',
    'Cloud',
    'Cyber Security',
    'Software Development',
    'Blockchain',
    'IoT',
    'DevOps',
  ], []);

  // Memoized stats
  const stats = useMemo(() => [
    {
      value: blogPosts.length.toString(),
      label: 'Total Articles',
      icon: BookOpen,
      className: 'w-8 h-8',
    },
    {
      value: blogPosts
        .reduce((acc, post) => acc + (post.views || 0), 0)
        .toString(),
      label: 'Total Views',
      icon: Eye,
      className: 'w-8 h-8',
    },
    {
      value: blogPosts
        .reduce((acc, post) => acc + (post.likes?.length || 0), 0)
        .toString(),
      label: 'Total Likes',
      icon: ThumbsUp,
      className: 'w-8 h-8',
    },
    {
      value: (categories.length - 1).toString(),
      label: 'Categories',
      icon: Tag,
      className: 'w-8 h-8',
    },
  ], [blogPosts, categories]);

  // API Functions
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/blog`);
      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyBlogs = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/blog/user/my-blogs`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setMyBlogs(data.blogs || []);
      }
    } catch (error) {
      console.error('Error fetching my blogs:', error);
    }
  };

  useEffect(() => {
    fetchBlogs();
    if (user) fetchMyBlogs();
  }, [user]);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'Date unknown';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Date unknown';
      }
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Date unknown';
    }
  };

  // Blog CRUD Operations
  const handleBlogSubmit = async e => {
    e.preventDefault();
    if (!user) {
      alert('Please login to create a blog');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      const url = editingBlog
        ? `${API_URL}/api/blog/${editingBlog._id}`
        : `${API_URL}/api/blog`;
      const method = editingBlog ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(blogForm),
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message || 'Blog saved successfully!');
        setShowCreateModal(false);
        setBlogForm({
          title: '',
          content: '',
          excerpt: '',
          category: 'Technology',
          tags: [],
          coverImage: '',
          status: 'published',
        });
        setEditingBlog(null);
        fetchBlogs();
        fetchMyBlogs();
      } else {
        alert(data.error || 'Failed to save blog');
      }
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteBlog = async blogId => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const response = await fetch(`${API_URL}/api/blog/${blogId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        alert('Blog deleted successfully');
        fetchBlogs();
        fetchMyBlogs();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete blog');
      }
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Network error. Please try again.');
    }
  };

  const handleLike = async blogId => {
    if (!user) {
      alert('Please login to like blogs');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/blog/${blogId}/like`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        await fetchBlogs();
        if (selectedBlog && selectedBlog._id === blogId) {
          const updatedBlog = blogPosts.find(b => b._id === blogId);
          if (updatedBlog) setSelectedBlog(updatedBlog);
        }
      }
    } catch (error) {
      console.error('Error liking blog:', error);
    }
  };

  const openBlog = async post => {
    try {
      const res = await fetch(`${API_URL}/api/blog/${post._id}`);
      if (!res.ok) return;
      const data = await res.json();
      setSelectedBlog(data.blog);
      fetchBlogs();
    } catch (e) {
      console.error('Error opening blog:', e);
    }
  };

  const makeSlug = blog => {
    const base = blog.title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')   // remove special chars
      .replace(/\s+/g, '-')           // spaces -> dashes
      .replace(/-+/g, '-');           // collapse dashes

    return `${base}`;
  };

  // Edit & Tag Management
  const handleEdit = blog => {
    setEditingBlog(blog);
    setBlogForm({
      title: blog.title || '',
      content: blog.content || '',
      excerpt: blog.excerpt || '',
      category: blog.category || 'Technology',
      tags: blog.tags || [],
      coverImage: blog.coverImage || '',
      status: blog.status || 'published',
    });
    setShowCreateModal(true);
  };

  const addTag = () => {
    if (
      tagInput.trim() &&
      !blogForm.tags.map(t => t.toLowerCase()).includes(tagInput.trim().toLowerCase())
    ) {
      setBlogForm(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
    }
    setTagInput('');
  };

  const removeTag = tagToRemove => {
    setBlogForm(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove),
    }));
  };

  // Filtering & Memoization
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        post.title?.toLowerCase().includes(term) ||
        post.content?.toLowerCase().includes(term) ||
        post.tags?.some(tag => tag.toLowerCase().includes(term));

      const matchesCategory =
        selectedCategory === 'all' || post.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [blogPosts, searchTerm, selectedCategory]);

  const featuredPost = blogPosts[0];

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-pulse rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium tracking-wide">Loading articles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      {/* ===== HERO SECTION ===== */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight font-sans">
                Community Blog
              </h1>
              <p className="text-xl md:text-2xl text-blue-100/90 mb-8 leading-relaxed font-light">
                Share your knowledge, insights, and experiences with the community.
              </p>

              {user ? (
                <div className="flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => {
                      setEditingBlog(null);
                      setBlogForm({
                        title: '',
                        content: '',
                        excerpt: '',
                        category: 'Technology',
                        tags: [],
                        coverImage: '',
                        status: 'published',
                      });
                      setShowCreateModal(true);
                    }}
                    className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl active:scale-95"
                    aria-label="Write a new blog"
                  >
                    <Plus className="w-5 h-5" />
                    Write a Blog
                  </button>
                  <button
                    onClick={() => setShowMyBlogs(!showMyBlogs)}
                    className="px-8 py-4 bg-blue-700/50 text-white rounded-xl font-semibold hover:bg-blue-800/50 transition-all duration-300 border-2 border-white/30 backdrop-blur-sm hover:backdrop-blur-md transform hover:-translate-y-1 active:scale-95"
                    aria-label={`My blogs (${myBlogs.length})`}
                  >
                    My Blogs ({myBlogs.length})
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl active:scale-95"
                >
                  Login to Write a Blog
                </button>
              )}
            </div>

            <div className="hidden lg:block animate-slide-right">
              <img
                src="https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=800&h=600&fit=crop"
                alt="Blog writing"
                className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== STATS SECTION ===== */}
      <section className="py-12 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-md p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 text-center animate-fade-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex justify-center text-blue-600 dark:text-blue-400 mb-4 animate-bounce-slow">
                  <stat.icon className={`${stat.className} drop-shadow-lg`} />
                </div>
                <div className="text-4xl font-bold text-gray-900 dark:text-white mb-2 font-mono tracking-tight">
                  {stat.value}
                </div>
                <div className="text-gray-600 dark:text-gray-400 font-medium tracking-wide">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SEARCH + FILTERS ===== */}
      <section className="sticky top-20 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-lg border-b border-gray-200/50 dark:border-gray-700/50 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="mb-6 animate-fade-in">
            <div className="relative max-w-2xl">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search articles by title, content, or tags..."
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:border-blue-600 focus:outline-none dark:bg-gray-800/50 dark:text-white shadow-lg backdrop-blur-sm transition-all duration-300 font-medium tracking-wide"
                aria-label="Search articles"
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-3 animate-slide-up">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:-translate-y-1 active:scale-95 shadow-lg ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-xl scale-105'
                    : 'bg-gray-100/80 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 backdrop-blur-sm'
                }`}
                aria-label={`Filter by ${category}`}
              >
                {category === 'all' ? 'All Posts' : category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MY BLOGS SECTION ===== */}
      {showMyBlogs && user && (
        <section className="py-12 bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-blue-900/20 dark:to-indigo-900/20 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8 animate-fade-in">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
                  My Blogs
                </h2>
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  Manage your published and draft articles
                </p>
              </div>
              <button
                onClick={() => setShowMyBlogs(false)}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-all duration-300 transform hover:scale-125"
                aria-label="Close my blogs"
              >
                <X className="w-7 h-7" />
              </button>
            </div>

            {myBlogs.length === 0 ? (
              <div className="text-center py-16 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-3xl shadow-2xl animate-fade-up">
                <BookOpen className="w-20 h-20 text-gray-400 mx-auto mb-6 animate-pulse" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                  No Blogs Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 font-medium">
                  Start sharing your knowledge with the community!
                </p>
                <button
                  onClick={() => {
                    setShowMyBlogs(false);
                    setEditingBlog(null);
                    setBlogForm({
                      title: '',
                      content: '',
                      excerpt: '',
                      category: 'Technology',
                      tags: [],
                      coverImage: '',
                      status: 'published',
                    });
                    setShowCreateModal(true);
                  }}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 inline-flex items-center gap-2 font-semibold"
                >
                  <Plus className="w-5 h-5" />
                  Write Your First Blog
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-up">
                {myBlogs.map((blog, index) => (
                  <div
                    key={blog._id}
                    className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-2xl overflow-hidden shadow-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 animate-fade-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {blog.coverImage && (
                      <div className="h-48 overflow-hidden">
                        <img
                          src={blog.coverImage}
                          alt={blog.title}
                          className="w-full h-full object-cover transform hover:scale-110 transition-transform duration-500"
                          onError={e => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span
                          className={`px-4 py-2 rounded-full text-sm font-semibold tracking-wide ${
                            blog.status === 'published'
                              ? 'bg-green-100/80 text-green-800 dark:bg-green-900/30 dark:text-green-300 backdrop-blur-sm'
                              : 'bg-yellow-100/80 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 backdrop-blur-sm'
                          }`}
                        >
                          {blog.status === 'published' ? 'Published' : 'Draft'}
                        </span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(blog)}
                            className="p-2 text-blue-600 hover:bg-blue-50/50 dark:hover:bg-blue-900/30 rounded-xl transition-all duration-300 transform hover:scale-110 backdrop-blur-sm"
                            title="Edit"
                            aria-label="Edit blog"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteBlog(blog._id)}
                            className="p-2 text-red-600 hover:bg-red-50/50 dark:hover:bg-red-900/30 rounded-xl transition-all duration-300 transform hover:scale-110 backdrop-blur-sm"
                            title="Delete"
                            aria-label="Delete blog"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 tracking-tight leading-snug">
                        {blog.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 font-medium leading-relaxed">
                        {blog.excerpt}
                      </p>

                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 font-medium">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          <span>{blog.views || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MessageCircle className="w-4 h-4" />
                          <span>{blog.comments?.length || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <ThumbsUp className="w-4 h-4" />
                          <span>{blog.likes?.length || 0}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{blog.readTime || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== FEATURED POST ===== */}
      {featuredPost && !showMyBlogs && !searchTerm && selectedCategory === 'all' && (
        <section className="py-20 animate-fade-in">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 mb-8 animate-slide-right">
              <TrendingUp className="w-8 h-8 text-blue-600 animate-pulse" />
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                Featured Post
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 items-center bg-gradient-to-br from-blue-50/80 to-indigo-50/80 dark:from-gray-800/80 dark:to-gray-700/80 backdrop-blur-md rounded-3xl overflow-hidden shadow-2xl border border-gray-200/50 dark:border-gray-700/50 transform hover:-translate-y-2 transition-all duration-500">
              {featuredPost.coverImage && (
                <div className="h-full overflow-hidden">
                  <img
                    src={featuredPost.coverImage}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover min-h-[400px] transform hover:scale-110 transition-transform duration-700"
                    onError={e => {
                      e.target.src =
                        'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=500&fit=crop';
                    }}
                  />
                </div>
              )}
              <div className="p-8 lg:p-12">
                <span className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-xl font-semibold tracking-wide shadow-lg">
                  {featuredPost.category}
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mt-6 mb-4 tracking-tight leading-tight">
                  {featuredPost.title}
                </h3>
                <p className="text-lg text-gray-700 dark:text-gray-300 mb-6 line-clamp-3 leading-relaxed font-medium">
                  {featuredPost.excerpt}
                </p>

                <div className="flex items-center flex-wrap gap-6 text-sm text-gray-600 dark:text-gray-400 mb-8 font-medium">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    <span className="font-semibold">
                      {featuredPost.authorName && featuredPost.authorName.trim() 
                        ? featuredPost.authorName 
                        : 'Anonymous Author'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>
                      {formatDate(featuredPost.publishedAt || featuredPost.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{featuredPost.readTime || '5 min'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    <span>{featuredPost.views || 0} views</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    <span>{featuredPost.comments?.length || 0} comments</span>
                  </div>
                </div>
                <button
                  onClick={() => openBlog(featuredPost)}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 active:scale-95 flex items-center gap-2 tracking-wide"
                >
                  <span>Read Full Article</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ===== BLOG GRID ===== */}
      {!showMyBlogs && (
        <section className="py-20 bg-gradient-to-b from-gray-50/50 to-white dark:from-gray-800/50 dark:to-gray-900/50 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 tracking-tight animate-fade-in">
              {searchTerm ? 'Search Results' : 'Latest Articles'} ({filteredPosts.length})
            </h2>

            {filteredPosts.length === 0 ? (
              <div className="text-center py-20 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md rounded-3xl shadow-2xl animate-fade-up">
                <Search className="w-20 h-20 text-gray-400 mx-auto mb-6 animate-pulse" />
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                  No Articles Found
                </h3>
                <p className="text-gray-600 dark:text-gray-400 font-medium">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <article
                    key={post._id}
                    className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-md rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 border border-gray-200/50 dark:border-gray-700/50 cursor-pointer animate-fade-up"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    onClick={() => {
                      const slug = makeSlug(post);
                      navigate(`/blog/${post._id}`);
                    }}
                  >
                    {post.coverImage && (
                      <div className="relative h-48 overflow-hidden group">
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                          onError={e => {
                            e.target.src =
                              'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=500&fit=crop';
                          }}
                        />
                        <div className="absolute top-4 right-4 bg-white/95 dark:bg-gray-900/95 px-4 py-2 rounded-xl text-sm font-semibold text-gray-900 dark:text-white backdrop-blur-md shadow-lg">
                          {post.category}
                        </div>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                    )}

                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-4 font-medium">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {formatDate(post.publishedAt || post.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{post.readTime || '5 min'}</span>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 line-clamp-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-300 tracking-tight leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3 leading-relaxed font-medium">
                        {post.excerpt}
                      </p>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                            <User className="w-5 h-5 text-white" />
                          </div>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white tracking-wide">
                            {post?.authorName && post?.authorName.trim() ? post?.authorName : 'Anonymous'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 font-medium">
                          <div className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            <span>{post.views || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            <span>{post.comments?.length || 0}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4" />
                            <span>{post.likes?.length || 0}</span>
                          </div>
                        </div>
                      </div>

                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {post.tags.slice(0, 3).map((tag, idx) => (
                            <span
                              key={idx}
                              className="flex items-center gap-2 px-3 py-1.5 bg-gray-100/80 dark:bg-gray-800/80 text-gray-600 dark:text-gray-400 text-xs rounded-xl font-medium backdrop-blur-sm"
                            >
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== CREATE/EDIT BLOG MODAL ===== */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
          onClick={() => {
            setShowCreateModal(false);
            setEditingBlog(null);
          }}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-3xl max-w-4xl w-full my-8 shadow-2xl animate-scale-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg z-10 rounded-t-3xl">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                {editingBlog ? 'Edit Blog' : 'Create New Blog'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingBlog(null);
                }}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-300 transform hover:scale-125"
                aria-label="Close modal"
              >
                <X className="w-7 h-7" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <form onSubmit={handleBlogSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                    Blog Title *
                  </label>
                  <input
                    type="text"
                    required
                    minLength={5}
                    value={blogForm.title}
                    onChange={e =>
                      setBlogForm(prev => ({ ...prev, title: e.target.value }))
                    }
                    className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:border-blue-600 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm transition-all duration-300 font-medium tracking-wide"
                    placeholder="Enter an engaging title (min 5 characters)"
                    aria-label="Blog title"
                  />
                </div>

                {/* Category + Status */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                      Category *
                    </label>
                    <div className="space-y-3">
                      <select
                        value={
                          categories.includes(blogForm.category)
                            ? blogForm.category
                            : ''
                        }
                        onChange={e => {
                          const value = e.target.value;
                          if (!value) return;
                          setBlogForm(prev => ({ ...prev, category: value }));
                        }}
                        className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm font-medium tracking-wide"
                        aria-label="Category"
                      >
                        <option value="">Choose a category</option>
                        {categories
                          .filter(c => c !== 'all')
                          .map(cat => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                      </select>
                      <input
                        type="text"
                        value={blogForm.category}
                        onChange={e =>
                          setBlogForm(prev => ({
                            ...prev,
                            category: e.target.value,
                          }))
                        }
                        className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm font-medium tracking-wide"
                        placeholder="Or type custom category"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        Select or type your own category.
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                      Status *
                    </label>
                    <select
                      value={blogForm.status}
                      onChange={e =>
                        setBlogForm(prev => ({
                          ...prev,
                          status: e.target.value,
                        }))
                      }
                      className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm font-medium tracking-wide"
                      aria-label="Publication status"
                    >
                      <option value="draft">Save as Draft</option>
                      <option value="published">Publish Now</option>
                    </select>
                  </div>
                </div>

                {/* Cover Image */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                    Cover Image URL (Optional)
                  </label>
                  <div className="relative">
                    <ImageIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="url"
                      value={blogForm.coverImage}
                      onChange={e =>
                        setBlogForm(prev => ({
                          ...prev,
                          coverImage: e.target.value,
                        }))
                      }
                      className="w-full pl-12 pr-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm font-medium tracking-wide"
                      placeholder="https://example.com/image.jpg"
                      aria-label="Cover image URL"
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                    Use Unsplash or any valid image URL.
                  </p>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                    Content * (Supports Markdown)
                  </label>
                  <textarea
                    required
                    minLength={50}
                    value={blogForm.content}
                    onChange={e =>
                      setBlogForm(prev => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    rows={8}
                    className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm resize-none font-mono text-sm tracking-wide leading-relaxed"
                    placeholder="Write your blog content here... (min 50 characters)"
                    aria-label="Blog content"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">
                    {blogForm.content
                      .trim()
                      .split(/\s+/)
                      .filter(Boolean).length}{' '}
                    words • approx{' '}
                    {Math.max(
                      1,
                      Math.ceil(
                        blogForm.content
                          .trim()
                          .split(/\s+/)
                          .filter(Boolean).length / 200
                      )
                    )}{' '}
                    min read
                  </p>
                </div>

                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                    Excerpt (Optional)
                  </label>
                  <textarea
                    value={blogForm.excerpt}
                    onChange={e =>
                      setBlogForm(prev => ({
                        ...prev,
                        excerpt: e.target.value,
                      }))
                    }
                    rows={3}
                    className="w-full px-5 py-4 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm resize-none font-medium tracking-wide"
                    placeholder="Brief summary shown in cards."
                    aria-label="Blog excerpt"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 tracking-wide">
                    Tags
                  </label>
                  <div className="flex gap-3 mb-3">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={e => setTagInput(e.target.value)}
                      onKeyPress={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          addTag();
                        }
                      }}
                      className="flex-1 px-5 py-3 border-2 border-gray-300/50 dark:border-gray-700/50 rounded-2xl focus:ring-4 focus:ring-blue-600/20 focus:outline-none dark:bg-gray-700/50 dark:text-white backdrop-blur-sm font-medium tracking-wide"
                      placeholder="Add a tag and press Enter"
                      aria-label="Add tag"
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      className="px-6 py-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all duration-300 transform hover:-translate-y-1 shadow-lg font-semibold"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {blogForm.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-100/80 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-xl text-sm font-medium backdrop-blur-sm shadow-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-blue-600 dark:hover:text-blue-200 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex gap-4 pt-6 border-t border-gray-200/50 dark:border-gray-700/50">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-2xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 disabled:opacity-50 disabled:hover:transform-none flex items-center justify-center gap-3"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span className="tracking-wide">Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5" />
                        <span className="tracking-wide">{editingBlog ? 'Update Blog' : 'Publish Blog'}</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateModal(false);
                      setEditingBlog(null);
                    }}
                    className="px-8 py-4 bg-gray-600 text-white rounded-2xl font-semibold hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ===== VIEW BLOG MODAL ===== */}
      {selectedBlog && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
          onClick={() => setSelectedBlog(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-3xl max-w-4xl w-full my-8 shadow-2xl animate-scale-up"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 flex items-center justify-between sticky top-0 bg-white/95 dark:bg-gray-800/95 backdrop-blur-lg z-10 rounded-t-3xl">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white truncate tracking-tight">
                {selectedBlog.title}
              </h2>
              <div className="flex items-center gap-4 ml-4">
                {user && (
                  <button
                    onClick={() => handleLike(selectedBlog._id)}
                    className={`flex items-center gap-3 px-5 py-2.5 rounded-xl transition-all duration-300 transform hover:-translate-y-1 shadow-lg ${
                      selectedBlog.likes?.includes(user.email)
                        ? 'bg-blue-600 text-white shadow-inner'
                        : 'bg-gray-100/80 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-blue-100/80 backdrop-blur-sm'
                    }`}
                    aria-label="Like blog"
                  >
                    <ThumbsUp className="w-5 h-5" />
                    <span className="font-semibold tracking-wide">{selectedBlog.likes?.length || 0}</span>
                  </button>
                )}

                {/* Share button */}
                <button
                  onClick={() => {
                    const blogUrl = `${window.location.origin}/blog/${selectedBlog._id}`;
                    navigator.clipboard.writeText(blogUrl);
                    setCopiedLink(true);
                    setTimeout(() => setCopiedLink(false), 2000);
                  }}
                  className="flex items-center gap-3 px-5 py-2.5 rounded-xl bg-gray-100/80 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 hover:bg-blue-100/80 dark:hover:bg-blue-900/30 transition-all duration-300 transform hover:-translate-y-1 shadow-lg backdrop-blur-sm font-medium"
                  aria-label="Copy share link"
                >
                  <span className="tracking-wide">{copiedLink ? 'Link copied!' : 'Share'}</span>
                </button>

                <button
                  onClick={() => setSelectedBlog(null)}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-all duration-300 transform hover:scale-125"
                  aria-label="Close"
                >
                  <X className="w-7 h-7" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-8 space-y-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {selectedBlog.coverImage && (
                <div className="relative overflow-hidden rounded-2xl">
                  <img
                    src={selectedBlog.coverImage}
                    alt={selectedBlog.title}
                    className="w-full h-64 object-cover transform hover:scale-105 transition-transform duration-500"
                    onError={e => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300" />
                </div>
              )}

              <div>
                <div className="mb-6">
                  <span className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-xl font-semibold tracking-wide shadow-lg inline-block">
                    {selectedBlog.category}
                  </span>
                </div>

                <div className="flex items-center gap-6 text-sm text-gray-600 dark:text-gray-400 mb-6 pb-6 border-b border-gray-200/50 dark:border-gray-700/50 font-medium">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    <span>{selectedBlog.authorName && selectedBlog.authorName.trim() 
                      ? selectedBlog.authorName 
                      : 'Anonymous Author'
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    <span>
                      {formatDate(selectedBlog.publishedAt || selectedBlog.createdAt)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span>{selectedBlog.readTime || '5 min'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    <span>{selectedBlog.views || 0}</span>
                  </div>
                </div>

                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed mb-6 font-medium tracking-wide">
                  {selectedBlog.content}
                </div>

                {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-gray-200/50 dark:border-gray-700/50">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 tracking-wide">
                      Tags
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedBlog.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-4 py-2 bg-gray-100/80 dark:bg-gray-700/80 text-gray-700 dark:text-gray-300 rounded-xl text-sm font-medium backdrop-blur-sm shadow-sm"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== ANIMATIONS ===== */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(50px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideRight {
          from { opacity: 0; transform: translateX(50px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes bounceSlow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-fade-up {
          animation: fadeUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-right {
          animation: slideRight 1s ease-out forwards;
        }
        
        .animate-slide-up {
          animation: slideUp 0.7s ease-out forwards;
          opacity: 0;
        }
        
        .animate-scale-up {
          animation: scaleUp 0.3s ease-out forwards;
        }
        
        .animate-bounce-slow {
          animation: bounceSlow 2s ease-in-out infinite;
        }
        
        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }

        @media (max-width: 768px) {
          .animate-slide-right {
            animation: fadeIn 0.6s ease-out forwards;
          }
        }
      `}</style>
    </div>
  );
}